import {
  EditedSceneData,
  Scene,
  Template,
  Templates,
  SceneData,
  UserEditedData,
  UserEditedPort,
} from './types/deluge';

type unMergeOptions = {
  context: Context;
  editedMergedObj: EditedSceneData;
  templates: Templates;
  scene?: Scene;
  template?: Template;
};

type Context = 'scene' | 'template';

export function unMergeSceneData(options: unMergeOptions): EditedSceneData {
  const { context, editedMergedObj, templates, scene, template } = options;

  // START Short circuits:

  if (context === 'template' && !template) {
    throw new Error(`Template needed with context of ${context}!`);
  }

  if (context === 'scene' && !scene) {
    throw new Error(`Scene needed with context of ${context}!`);
  }

  if (!scene && !template) {
    throw new Error('scene or template not found!');
  }

  let topLevelTemplate: Template | undefined;

  if (context === 'template' && template) {
    topLevelTemplate = template;
  }

  if (context === 'scene' && scene) {
    topLevelTemplate = templates[scene.scene_name];
  }

  if (!topLevelTemplate) {
    throw new Error(
      'topLevelTemplate not found: Did you provide the correct level?, Scene or Template?'
    );
  }

  const svarsToCompare: SceneData | undefined =
    scene?.svars || template?.scene_data;

  if (!svarsToCompare) {
    throw new Error('Neither scene nor template data found!');
  }

  // END Short circuits

  // We're going to mutate the mergedObj, so we need to make a copy of it
  const copyOfMerged: EditedSceneData = JSON.parse(
    JSON.stringify(editedMergedObj)
  );

  // console.log('svarsToCompare', svarsToCompare);

  lowerLevelUnMerge({
    context: context,
    newStateNode: copyOfMerged,
    currentStateNode: svarsToCompare,
    topLevelTemplate,
    templates,
    nestedLevel: false,
  });

  return copyOfMerged;
}

type lowerLevelUnMergeOptions = {
  context: Context;
  newStateNode: EditedSceneData;
  currentStateNode: SceneData;
  topLevelTemplate: Template;
  templates: Templates;
  nestedLevel: boolean;
};

// Scenarios:

// Questions?

// Key is in SvarsToCompareNode, but not in mergeNode

// How do we know that we should add or remove it?
// svarsToCompareNode is the current state. If the current state is empty.
// Then that key would not have been created from the current state, and must've been added via inheritance? Or a User change?

// Steps:

// Iterate through the keys in the newStateNode

// 1. If a key is not in the currentStateNode:
//      1.1  and matches the inherited value, then remove it. It was inherited.
//      1.2  and does not match the inherited value, then keep it. It was added by a user.

// 2. If a key is in the currentStateNode:
//      2.1  and differs from the newStateNode, then keep it. It was changed by a user.

// Explicit Addition or Delete:
// As it is impossible to know if a key was 'reset' to the inherited value, or if it was deleted by a user, userIntent is conveyed via the userIntent key.

// If a value is different from the lower sources, then a user must have changed it

// Check if the keys matches the inherited values.
function lowerLevelUnMerge(options: lowerLevelUnMergeOptions) {
  const {
    context,
    newStateNode,
    currentStateNode,
    topLevelTemplate,
    templates,
    nestedLevel,
  } = options;

  cleanNewStateNode(newStateNode);
  handleUserIntentsRemovals(newStateNode);

  for (const [key, newStateValue] of Object.entries(newStateNode)) {
    //  Putting this at the start so that this always runs.
    handleNestedSceneData(context, newStateValue, templates);

    // We can now use short circuits.
    if (newStateValue.userIntent === 'keep') continue;
    if (newStateValue.keyInherited === false) continue;
    if (key in currentStateNode) continue;

    if (nestedLevel) {
      //@ts-ignore
      delete newStateValue.value;
      //@ts-ignore
      delete newStateValue.type;
      delete newStateValue.keyInherited;
      if (Object.keys(newStateValue).length === 0) delete newStateNode[key];
    }

    // We don't want to break the connection, so we continue.
    if ('scene_data' in newStateValue) continue;

    delete newStateNode[key];
  }

  removeSpecialKeys(newStateNode);
  return newStateNode;
}

function handleNestedSceneData(
  context: Context,
  newStateValue: UserEditedData | UserEditedPort,
  templates: Templates
) {
  if (typeof newStateValue.value !== 'string') return;
  if (!('scene_data' in newStateValue)) return;

  const nextTemplate = templates[newStateValue.value];
  const nextNewStateNode = newStateValue['scene_data'];

  lowerLevelUnMerge({
    context,
    newStateNode: nextNewStateNode,
    currentStateNode: {},
    topLevelTemplate: nextTemplate,
    templates,
    nestedLevel: true,
  });
}

function cleanNewStateNode(newStateNode: EditedSceneData) {
  const subKeysToKeep = [
    'value',
    'type',
    'scene_data',
    'userIntent',
    'keyInherited',
  ];

  //  Clean newStateNode
  for (const newStateValue of Object.values(newStateNode)) {
    // Remove any keys that are not in subKeysToKeep
    for (const subKey in newStateValue) {
      if (!subKeysToKeep.includes(subKey)) {
        // @ts-ignore
        delete newStateValue[subKey];
      }
    }
  }
}

function handleUserIntentsRemovals(newStateNode: EditedSceneData) {
  for (const [key, value] of Object.entries(newStateNode)) {
    if (value.userIntent === 'remove') {
      delete newStateNode[key];
    }
  }
}

function removeSpecialKeys(newStateNode: EditedSceneData) {
  for (const value of Object.values(newStateNode)) {
    if ('userIntent' in value) {
      delete value['userIntent'];
    }

    if ('keyInherited' in value) {
      delete value['keyInherited'];
    }
  }
}
